"use client";

import * as React from "react";
import { Line, LineChart, CartesianGrid, XAxis, YAxis } from "recharts";

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../components/ui/Card";
import {
    ChartContainer,
    ChartLegend,
    ChartLegendContent,
    ChartTooltip,
    ChartTooltipContent,
} from "../../components/ui/chart";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/Select";

const LINE_ANIMATION_MS = 1500;

const orderedSeriesKeys = (chartConfig) => [
    ...Object.keys(chartConfig).filter((c) => c !== "occupancy"),
    ...(Object.keys(chartConfig).includes("occupancy") ? ["occupancy"] : []),
];

export const ShadLineChart = React.memo(function Component({
    timeRange,
    setTimeRange,
    filterDayOptions,
    lineDeviceName,
    setLineDeviceName,
    deviceNames,
    locationHebrewName,
    chartConfig,
    filteredData,
    chartContainerStyle = "aspect-auto h-[260px] w-full overflow-hidden",
    dots = false,
    cardDescription,
}) {
    const selectionKey = `${lineDeviceName || ""}-${timeRange || ""}`;
    const prevSelectionRef = React.useRef(selectionKey);
    const animatingRef = React.useRef(false);
    const shouldAnimateRef = React.useRef(false);
    const filteredRef = React.useRef(filteredData);
    filteredRef.current = filteredData;

    const [displayData, setDisplayData] = React.useState(filteredData);
    const [displayMeta, setDisplayMeta] = React.useState({
        lineDeviceName,
        cardDescription,
    });

    // Morph on lot/range switch — flag animation via ref so we don't re-render at end (no bump).
    React.useEffect(() => {
        const selectionChanged = prevSelectionRef.current !== selectionKey;

        if (selectionChanged && filteredRef.current?.length > 0) {
            animatingRef.current = true;

            let innerRaf = 0;
            const outerRaf = requestAnimationFrame(() => {
                innerRaf = requestAnimationFrame(() => {
                    shouldAnimateRef.current = true;
                    setDisplayData(filteredRef.current);
                    setDisplayMeta({ lineDeviceName, cardDescription });
                    prevSelectionRef.current = selectionKey;

                    window.setTimeout(() => {
                        shouldAnimateRef.current = false;
                        animatingRef.current = false;
                    }, LINE_ANIMATION_MS);
                });
            });

            return () => {
                cancelAnimationFrame(outerRaf);
                if (innerRaf) cancelAnimationFrame(innerRaf);
            };
        }

        if (!selectionChanged && !animatingRef.current) {
            setDisplayData(filteredRef.current);
            setDisplayMeta({ lineDeviceName, cardDescription });
        }

        return undefined;
    }, [selectionKey, filteredData, lineDeviceName, cardDescription]);

    return (
        <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:justify-end items-center gap-2 space-y-2 sm:space-y-0 border-b py-3 text-center sm:text-right">
                <div className="flex flex-col sm:flex-row items-center w-full sm:w-auto justify-center sm:justify-end gap-2">
                    {timeRange && setTimeRange && filterDayOptions && (
                        <Select value={timeRange} onValueChange={setTimeRange}>
                            <SelectTrigger
                                className="h-7 w-[150px] rounded-lg pl-2.5"
                                aria-label="Select a value"
                            >
                                <SelectValue placeholder="Last 6 months" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                                {filterDayOptions.map(({ value, filterDayOptionComponent }, i) => (
                                    <SelectItem value={value} key={i} className="rounded-lg">
                                        <div className="flex flex-row gap-1">
                                            {filterDayOptionComponent}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}

                    {lineDeviceName && setLineDeviceName && deviceNames && locationHebrewName && (
                        <Select value={lineDeviceName} onValueChange={setLineDeviceName}>
                            <SelectTrigger
                                className="h-7 w-[130px] rounded-lg pl-2.5"
                                aria-label="Select a value"
                            >
                                <SelectValue placeholder="Select month" />
                            </SelectTrigger>
                            <SelectContent align="end" className="rounded-xl">
                                {deviceNames.map((key) => (
                                    <SelectItem key={key} value={key} className="rounded-lg [&_span]:flex">
                                        <div className="flex items-center gap-2 text-xs">
                                            {locationHebrewName[key]}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                </div>
            </CardHeader>

            <CardHeader className="flex items-center gap-2 space-y-0 py-5 sm:flex-row">
                <div className="grid flex-1 gap-1 text-right sm:text-right">
                    {locationHebrewName && (
                        <CardTitle>
                            {locationHebrewName[displayMeta.lineDeviceName] || displayMeta.lineDeviceName}
                        </CardTitle>
                    )}
                    <div className="flex flex-1 gap-1 text-right justify-end">
                        {timeRange && displayMeta.cardDescription && (
                            <CardDescription>{displayMeta.cardDescription}</CardDescription>
                        )}
                    </div>
                </div>
            </CardHeader>

            <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
                {chartConfig && displayData && (
                    <ChartContainer
                        config={chartConfig}
                        className={`mx-auto w-full ${chartContainerStyle}`}
                    >
                        <LineChart
                            data={displayData}
                            margin={{ top: 8, right: 12, left: 4, bottom: 0 }}
                        >
                            <CartesianGrid vertical={false} />
                            <YAxis
                                domain={[0, "auto"]}
                                allowDataOverflow={false}
                                tickLine={false}
                                axisLine={false}
                                tickMargin={6}
                                width={40}
                                tick={{ fontSize: 10, fill: "#6b7280" }}
                                tickFormatter={(value) => Number(value).toLocaleString("he-IL")}
                                allowDecimals={false}
                            />
                            <XAxis
                                dataKey="date"
                                tickLine={false}
                                axisLine={false}
                                tickMargin={8}
                                minTickGap={32}
                                tickFormatter={(value) => {
                                    const date = new Date(value);
                                    return date.toLocaleDateString("he-IS", {
                                        weekday: "short",
                                        day: "numeric",
                                        hour: "numeric",
                                        minute: "2-digit",
                                    });
                                }}
                            />
                            <ChartTooltip
                                cursor={false}
                                content={(tooltipProps) => {
                                    const payload = Array.isArray(tooltipProps.payload)
                                        ? [...tooltipProps.payload].sort((a, b) => {
                                              const rank = (key) => {
                                                  if (key === "occupancy") return 0;
                                                  if (key === "entering") return 1;
                                                  return 2;
                                              };
                                              return rank(a.dataKey) - rank(b.dataKey);
                                          })
                                        : tooltipProps.payload;
                                    return (
                                        <ChartTooltipContent
                                            {...tooltipProps}
                                            payload={payload}
                                            labelFormatter={(value) =>
                                                new Date(value).toLocaleDateString("he-IS", {
                                                    weekday: "short",
                                                    day: "numeric",
                                                    hour: "numeric",
                                                    minute: "2-digit",
                                                })
                                            }
                                            indicator="dot"
                                        />
                                    );
                                }}
                            />
                            {orderedSeriesKeys(chartConfig).map((c) => (
                                <Line
                                    key={c}
                                    dataKey={c}
                                    type="monotone"
                                    stroke={`var(--color-${c})`}
                                    strokeWidth={2}
                                    dot={dots}
                                    isAnimationActive={shouldAnimateRef.current}
                                    animationDuration={LINE_ANIMATION_MS}
                                    strokeDasharray={c === "occupancy" ? "5 5" : undefined}
                                />
                            ))}
                            <ChartLegend content={<ChartLegendContent />} />
                        </LineChart>
                    </ChartContainer>
                )}
            </CardContent>
        </Card>
    );
});
